import React, { useState } from 'react';
import Helmet from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { t } from '@lingui/macro';
import { useQueryClient } from '@tanstack/react-query';

import { Box, Heading } from '@rover/kibble/core';
import Section from '@rover/kibble/patterns/Section';
import BackButtonWithFormState from '@rover/react-lib/src/pages/provider-profile/components/BackButtonWithFormState';
import { useApiCatProviderProfilePartialUpdate } from '@rover/rsdk/src/apiClient/latest/api-cat-provider-profile-partial-update';
import {
  getApiCatProviderProfileRetrieveQueryKey,
  useApiCatProviderProfileRetrieve,
} from '@rover/rsdk/src/apiClient/latest/api-cat-provider-profile-retrieve';
import { useApiProviderProfileModuleCreate } from '@rover/rsdk/src/apiClient/latest/api-provider-profile-module-create';
import {
  getApiProviderProfileModuleRetrieveQueryKey,
  useApiProviderProfileModuleRetrieve,
} from '@rover/rsdk/src/apiClient/latest/api-provider-profile-module-retrieve';
import type { ExperienceModule } from '@rover/rsdk/src/apiClient/latest/model/experienceModule';
import type { RetrieveCatProviderProfile } from '@rover/rsdk/src/apiClient/latest/model/retrieveCatProviderProfile';
import { HttpError } from '@rover/rsdk/src/errors';
import { Logger } from '@rover/rsdk/src/modules/Logging';
import { useProviderPetTypes } from '@rover/shared/js/hooks/useProviderPetTypes';

import { MODULE_KEYS } from '../../constants/moduleKeys';
import { ReturnQueryParam } from '../../types/ServiceForm';
import useBackRouteParams from '../hooks/useBackRouteParams';

import type { SkillsFormData } from './components';
import { SkillsForm, SkillsFormShimmer } from './components';

const SkillsPage: React.FC = () => {
  const navigate = useNavigate();
  const [showBackModal, setShowBackModal] = useState(false);
  const [submissionErrors, setSubmissionErrors] = useState<Record<string, string[] | string>>({});
  const queryClient = useQueryClient();

  const { backRoute } = useBackRouteParams(ReturnQueryParam.ExperienceAdded);

  const {
    offersCatCare,
    offersDogCare,
    isLoading: isLoadingProviderPetTypes,
  } = useProviderPetTypes();

  // fetch dog skills from experience module
  const { data: experienceData, isLoading: isLoadingExperience } =
    useApiProviderProfileModuleRetrieve(MODULE_KEYS.EXPERIENCE);

  // fetch cat skills from cat profile
  const { data: catProfileData, isLoading: isLoadingCatProfile } =
    useApiCatProviderProfileRetrieve();

  // mutations for dog skills — uses the skills-only partial endpoint
  const { mutate: mutateExperience, isLoading: isMutatingExperience } =
    useApiProviderProfileModuleCreate({
      mutation: {
        onSuccess: (response) => {
          queryClient.setQueryData(
            getApiProviderProfileModuleRetrieveQueryKey(MODULE_KEYS.EXPERIENCE),
            (oldData: typeof experienceData) => {
              if (!oldData) return oldData;
              return {
                ...oldData,
                data: {
                  ...oldData.data,
                  ...response.data,
                },
              };
            }
          );
        },
        onError: (error) => {
          Logger.info('Skills form error - experience module', {
            errorMessage: error.message,
          });

          if (error instanceof HttpError && error.rawResponse?.data) {
            setSubmissionErrors((prev) => ({
              ...prev,
              ...(error.rawResponse?.data as Record<string, string[] | string>),
            }));
          }
        },
      },
    });

  // mutations for cat skills - partial update
  const { mutate: updateCatProfile, isLoading: isUpdatingCatProfile } =
    useApiCatProviderProfilePartialUpdate({
      mutation: {
        onSuccess: (response) => {
          // Update the cat profile cache with the response
          queryClient.setQueryData(getApiCatProviderProfileRetrieveQueryKey(), response);
        },
        onError: (error) => {
          Logger.info('Skills form error - cat profile partial update', {
            errorMessage: error.message,
          });

          if (error instanceof HttpError && error.rawResponse?.data) {
            setSubmissionErrors((prev) => ({
              ...prev,
              ...(error.rawResponse?.data as Record<string, string[] | string>),
            }));
          }
        },
      },
    });

  const experienceModule = experienceData?.data as ExperienceModule | undefined;
  const catProfile = catProfileData as RetrieveCatProviderProfile | undefined;

  // prepare initial form data from API responses
  const initialFormData: SkillsFormData = {
    dogSkills: [
      ...(experienceModule?.firstAidCpr ? ['first_aid_cpr'] : []),
      ...(experienceModule?.canProvideOralMedication ? ['can_provide_oral_medication'] : []),
      ...(experienceModule?.canProvideInjectedMedication
        ? ['can_provide_injected_medication']
        : []),
      ...(experienceModule?.seniorDogCareExperience ? ['senior_dog_care_experience'] : []),
      ...(experienceModule?.specialNeedsDogCareExperience
        ? ['special_needs_dog_care_experience']
        : []),
      ...(experienceModule?.canProvideDailyExercise ? ['can_provide_daily_exercise'] : []),
    ],
    catSkills: [
      ...(catProfile?.canProvideOralMedication ? ['can_provide_oral_medication'] : []),
      ...(catProfile?.canProvideInjectedMedication ? ['can_provide_injected_medication'] : []),
    ],
  };

  const handleSubmit = async (formData: SkillsFormData): Promise<void> => {
    const mutations: Array<Promise<unknown>> = [];

    try {
      // Update dog profile module if offering dog care
      if (offersDogCare) {
        mutations.push(
          new Promise((resolve, reject) => {
            mutateExperience(
              {
                moduleSlug: MODULE_KEYS.EXPERIENCE_SKILLS,
                data: {
                  firstAidCpr: formData.dogSkills.includes('first_aid_cpr'),
                  canProvideOralMedication: formData.dogSkills.includes(
                    'can_provide_oral_medication'
                  ),
                  canProvideInjectedMedication: formData.dogSkills.includes(
                    'can_provide_injected_medication'
                  ),
                  seniorDogCareExperience: formData.dogSkills.includes(
                    'senior_dog_care_experience'
                  ),
                  specialNeedsDogCareExperience: formData.dogSkills.includes(
                    'special_needs_dog_care_experience'
                  ),
                  canProvideDailyExercise: formData.dogSkills.includes(
                    'can_provide_daily_exercise'
                  ),
                } as ExperienceModule,
              },
              {
                onSuccess: resolve,
                onError: reject,
              }
            );
          })
        );
      }

      // Update cat profile if offering cat care and profile exists
      if (offersCatCare && catProfile) {
        mutations.push(
          new Promise((resolve, reject) => {
            updateCatProfile(
              {
                data: {
                  canProvideOralMedication: formData.catSkills.includes(
                    'can_provide_oral_medication'
                  ),
                  canProvideInjectedMedication: formData.catSkills.includes(
                    'can_provide_injected_medication'
                  ),
                },
              },
              {
                onSuccess: resolve,
                onError: reject,
              }
            );
          })
        );
      }

      await Promise.all(mutations);
      setSubmissionErrors({});

      // Use React Router navigation to preserve cache state
      navigate(backRoute);
    } catch (error) {
      Logger.info('Skills form submission failed', {
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  };

  const isLoading = isLoadingExperience || isLoadingCatProfile || isLoadingProviderPetTypes;
  const isMutating = isMutatingExperience || isUpdatingCatProfile;

  return (
    <>
      <Helmet>
        <title>{t`Skills`}</title>
      </Helmet>

      <Section maxWidth={['100%', '640px']} data-qa-id="skills-page" py="6x">
        <BackButtonWithFormState href={backRoute} skipModal={!showBackModal} />

        <Box py="8x">
          <Heading size="400">{t`Skills`}</Heading>

          {isLoading ? (
            <SkillsFormShimmer />
          ) : (
            <SkillsForm
              initialData={initialFormData}
              isSubmitting={isMutating}
              onSubmit={handleSubmit}
              setShowBackModal={setShowBackModal}
              submissionErrors={submissionErrors}
              offersCatCare={offersCatCare}
              offersDogCare={offersDogCare}
              catProfile={catProfile}
            />
          )}
        </Box>
      </Section>
    </>
  );
};

export default SkillsPage;
